import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";

// Components
import { TranslateModule } from "@ngx-translate/core";
import { AlertComponent } from "./components/alert/alert.component";
import { CargoActionMenuComponent } from "./components/cargo-action-menu/cargo-action-menu.component";
import { CargoCardComponent } from "./components/cargo-card/cargo-card.component";
import { CargoModalComponent } from "./components/cargo-modal/cargo-modal.component";
import { CargoSelectComponent } from "./components/cargo-select/cargo-select.component";
import { CarouselComponent } from "./components/carousel/carousel.component";
import { CheckboxInputComponent } from "./components/checkbox-input/checkbox-input.component";
import { ColorPickerComponent } from "./components/color-picker/color-picker.component";
import { ConfirmationModalComponent } from "./components/confirmation-modal/confirmation-modal.component";
import { CustomSelectComponent } from "./components/custom-select/custom-select.component";
import { DragInfoModalComponent } from "./components/drag-info-modal/drag-info-modal.component";
import { FakeDisabledInputComponent } from "./components/fake-disabled-input/fake-disabled-input.component";
import { FileInputComponent } from "./components/file-input/file-input.component";
import { FilterChipComponent } from "./components/filter-chip/filter-chip/filter-chip.component";
import { LoadingBackdropComponent } from "./components/loading-backdrop/loading-backdrop/loading-backdrop.component";
import { MultipleTextSwitchComponent } from "./components/multiple-text-switch/multiple-text-switch.component";
import { NumberInputComponent } from "./components/number-input/number-input.component";
import { RadioInputComponent } from "./components/radio-input/radio-input.component";
import { SvgIconComponent } from "./components/svg-icon/svg-icon.component";
import { SwitchInputComponent } from "./components/switch-input/switch-input.component";
import { SwitchTextComponent } from "./components/switch-text-input/switch-text-input.component";
import { TabsComponent } from "./components/tabs/tabs.component";
import { TextFieldComponent } from "./components/text-field/text-field.component";
import { TextInputComponent } from "./components/text-input/text-input.component";
import { ToggleButtonsComponent } from "./components/toggle-buttons/toggle-buttons/toggle-buttons.component";
import { TransferListComponent } from "./components/transfer-list/transfer-list.component";
import { ClipboardDirective } from "./directives/clipboard.directive";
import { HasAllPermissionsDirective, HasAnyPermissionsDirective, PermissionsDirective } from "./directives/permission.directive";
import { TooltipDirective } from "./directives/tooltip.directive";
import { ArrayFromNumberPipe } from "./pipes/array-from-number.pipe";
import { HasPermissionPipe } from "./pipes/has-permission.pipe";
import { MapToArrayPipe } from "./pipes/map-to-array.pipe";
import { NullOrUndefinedPipe } from "./pipes/null-or-undefined.pipe";
import { ObjectLengthPipe } from "./pipes/object-length.pipe";
import { OrderStatusPipe } from "./pipes/order-status.pipe";
import {
  ParseBoxDate,
  ParseCancelationMoment,
  ParseCancelationReason,
  ParseCurrency,
  ParseDate,
  ParseDistance,
  ParsePaymentMethodIconPipe,
  ParsePaymentMethodPipe,
  ParseTimePipe,
  ParseTransportTypePipe,
  ParseVehiclePipe,
} from "./pipes/parse.pipe";
import { RemoveSpacesPipe } from "./pipes/remove-spaces.pipe";
import { SafeHtmlPipe } from "./pipes/safe-html.pipe";
import { SafeUrlPipe } from "./pipes/safe-url.pipe";
import { ShortenIdPipe } from "./pipes/shorten-id.pipe";
import { ShortenStringPipe } from "./pipes/shorten-string.pipe";
import { StringBuilderPipe } from "./pipes/string-builder.pipe";
import { HelpCenterCtaComponent } from "./widgets/help-center-cta/help-center-cta.component";
import { OrderTimelineComponent } from "./widgets/order-timeline/order-timeline.component";
import { HorizontalTimelineComponent } from "./widgets/order-timeline/variants/horizontal/horizontal.component";
import { VerticalTimelineComponent } from "./widgets/order-timeline/variants/vertical/vertical.component";
import { PickingPointMultiselectFacade } from "./widgets/picking-point-multiselect/facade/picking-point-multiselect.facade";
import { PickingPointMultiselectComponent } from "./widgets/picking-point-multiselect/picking-point-multiselect.component";
import { ReportsWindowFacade } from "./widgets/reports-window/facade/reports-window.facade";
import { ReportsWindowComponent } from "./widgets/reports-window/reports-window.component";
import { RetailerSelectorComponent } from "./widgets/retailer-selector/retailer-selector.component";
import { RolesDialogComponent } from "./widgets/roles-dialog/roles-dialog.component";
import { SaveBarComponent } from "./widgets/save-bar/save-bar.component";

@NgModule({
  imports: [CommonModule, TranslateModule],
  declarations: [
    AlertComponent,
    ArrayFromNumberPipe,
    CargoCardComponent,
    CargoModalComponent,
    CargoSelectComponent,
    CarouselComponent,
    CheckboxInputComponent,
    ClipboardDirective,
    ColorPickerComponent,
    ConfirmationModalComponent,
    CustomSelectComponent,
    DragInfoModalComponent,
    FakeDisabledInputComponent,
    FileInputComponent,
    FilterChipComponent,
    HelpCenterCtaComponent,
    HorizontalTimelineComponent,
    LoadingBackdropComponent,
    MapToArrayPipe,
    MultipleTextSwitchComponent,
    NullOrUndefinedPipe,
    NumberInputComponent,
    ObjectLengthPipe,
    OrderStatusPipe,
    OrderTimelineComponent,
    ParseBoxDate,
    ParseCancelationMoment,
    ParseCancelationReason,
    ParseCurrency,
    ParseDate,
    ParseDistance,
    ParsePaymentMethodIconPipe,
    ParsePaymentMethodPipe,
    ParseTimePipe,
    ParseTransportTypePipe,
    ParseVehiclePipe,
    PermissionsDirective,
    HasAllPermissionsDirective,
    HasAnyPermissionsDirective,
    PickingPointMultiselectComponent,
    RadioInputComponent,
    RemoveSpacesPipe,
    HasPermissionPipe,
    ReportsWindowComponent,
    RetailerSelectorComponent,
    RolesDialogComponent,
    SafeHtmlPipe,
    SafeUrlPipe,
    SaveBarComponent,
    ShortenIdPipe,
    ShortenStringPipe,
    StringBuilderPipe,
    SvgIconComponent,
    SwitchInputComponent,
    SwitchTextComponent,
    TabsComponent,
    TextFieldComponent,
    TextInputComponent,
    ToggleButtonsComponent,
    TooltipDirective,
    TransferListComponent,
    VerticalTimelineComponent,
    CargoActionMenuComponent,
  ],
  exports: [
    AlertComponent,
    ArrayFromNumberPipe,
    CargoCardComponent,
    CargoModalComponent,
    CargoSelectComponent,
    CarouselComponent,
    CheckboxInputComponent,
    ClipboardDirective,
    ColorPickerComponent,
    ConfirmationModalComponent,
    CustomSelectComponent,
    DragInfoModalComponent,
    FakeDisabledInputComponent,
    FileInputComponent,
    FilterChipComponent,
    HelpCenterCtaComponent,
    HorizontalTimelineComponent,
    LoadingBackdropComponent,
    MapToArrayPipe,
    MultipleTextSwitchComponent,
    NullOrUndefinedPipe,
    NumberInputComponent,
    ObjectLengthPipe,
    OrderStatusPipe,
    OrderTimelineComponent,
    ParseBoxDate,
    ParseCancelationMoment,
    ParseCancelationReason,
    ParseCurrency,
    ParseDate,
    ParseDistance,
    ParsePaymentMethodIconPipe,
    ParsePaymentMethodPipe,
    HasPermissionPipe,
    ParseTimePipe,
    ParseTransportTypePipe,
    ParseVehiclePipe,
    PermissionsDirective,
    HasAllPermissionsDirective,
    HasAnyPermissionsDirective,
    PickingPointMultiselectComponent,
    RadioInputComponent,
    RemoveSpacesPipe,
    ReportsWindowComponent,
    RetailerSelectorComponent,
    RolesDialogComponent,
    SafeHtmlPipe,
    SafeUrlPipe,
    SaveBarComponent,
    ShortenIdPipe,
    ShortenStringPipe,
    ShortenStringPipe,
    StringBuilderPipe,
    SwitchInputComponent,
    SwitchTextComponent,
    TabsComponent,
    TextFieldComponent,
    TextInputComponent,
    ToggleButtonsComponent,
    TooltipDirective,
    TransferListComponent,
    VerticalTimelineComponent,
    CargoActionMenuComponent,
  ],
  providers: [PickingPointMultiselectFacade, ReportsWindowFacade],
})
export class SharedModule {}
